import { useMemo } from "react";
import { IHackathonResponse } from "api/hackathon";
import { getDisabledTimezone, getTzFormat, initDate } from "utils/dateUtils";

const isBefore = (init: any, compare: any): boolean => {
  return Boolean(init && compare && init.isBefore(compare));
};
const isAfter = (init: any, compare: any): boolean => {
  return Boolean(init && compare && init.isAfter(compare));
};

export const usePublishCondition = (hackathon?: IHackathonResponse) => {
  return useMemo(() => {
    let passEssential = !!hackathon?.essentials?.tagLine;
    let passEligibility = !!hackathon?.eligibility?.community;
    let passHackathonSite = !!hackathon?.site?.mainDescription;
    let passSubmission = !!hackathon?.submission?.deadline;
    const { value: tz } = getTzFormat(hackathon?.essentials?.timeZone);

    const startDate = initDate(hackathon?.submission?.startDate, tz);
    const deadline = initDate(hackathon?.submission?.deadline, tz);
    const startVoteDate = initDate(hackathon?.vote?.startVote, tz);
    const endVoteDate = initDate(hackathon?.vote?.endVote, tz);
    const startJudgeDate = initDate(
      hackathon?.judging?.judgingDate?.startDate,
      tz
    );
    const endJudgeDate = initDate(hackathon?.judging?.judgingDate?.endDate, tz);
    const announceDate = initDate(hackathon?.prizes?.onDate, tz);
    const today =
      hackathon?.status === "draft" ? getDisabledTimezone(tz) : null; // validate today only draft status

    if (
      isBefore(startDate, today) ||
      isAfter(startDate, deadline) ||
      isAfter(startDate, startVoteDate) ||
      isAfter(deadline, endVoteDate) ||
      isAfter(deadline, startJudgeDate) ||
      isAfter(deadline, announceDate)
    ) {
      passSubmission = false;
    }

    let passVoting = true;
    if (startVoteDate) {
      if (
        isBefore(startVoteDate, startDate) ||
        isAfter(startVoteDate, endVoteDate) ||
        isBefore(endVoteDate, deadline) ||
        isAfter(endVoteDate, announceDate)
      ) {
        passVoting = false;
      }
    }

    // Judge validate
    let passJudging =
      !!hackathon?.judging?.judgingType &&
      hackathon?.judging?.judges &&
      hackathon?.judging?.criteria &&
      hackathon?.judging?.judges?.length !== 0 &&
      hackathon?.judging?.criteria?.length !== 0;

    if (hackathon?.judging?.judgingType === "online") {
      if (
        isBefore(startJudgeDate, deadline) ||
        isAfter(startJudgeDate, endJudgeDate) ||
        isAfter(endJudgeDate, announceDate) ||
        hackathon?.judging?.judges?.some((x) => !x.email)
      ) {
        passJudging = false;
      }
    }

    let passPrizes =
      !!hackathon?.prizes?.onDate &&
      hackathon?.prizes?.awards &&
      hackathon?.prizes?.awards.length !== 0;

    if (
      isBefore(announceDate, today) ||
      isBefore(announceDate, deadline) ||
      isBefore(announceDate, endVoteDate) ||
      isBefore(announceDate, endJudgeDate)
    ) {
      passPrizes = false;
    }

    const passObject = {
      passEssential,
      passEligibility,
      passHackathonSite,
      passSubmission,
      passJudging,
      passPrizes,
      passVoting,
    };

    const isPassAll = Object.values(passObject).every((pass) => pass);

    return {
      ...passObject,
      isPassAll,
      canPublish:
        isPassAll &&
        (hackathon?.status === "draft" || hackathon?.status === "rejected"),
      startDate,
      deadline,
      startVoteDate,
      endVoteDate,
      startJudgeDate,
      endJudgeDate,
      announceDate,
    };
  }, [hackathon]);
};
